import {type KeyboardEvent, type ReactNode} from 'react';

import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {Box, Collapse, Typography} from '@mui/material';

import {greenColors, orangeColors} from '../../layouts/theme/designSystem';

type SectionVariant = 'default' | 'secondary';

interface Props {
  title: string;
  children: ReactNode;
  collapsible?: boolean;
  open?: boolean;
  onToggle?: () => void;
  metaText?: string;
  titleSuffix?: ReactNode;
  variant?: SectionVariant;
}

const sectionShellSx = {
  borderRadius: '8px',
  overflow: 'hidden',
} as const;

const sectionHeaderRowSx = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 0.5,
  minHeight: 32,
  py: 0.5,
  px: 1,
  userSelect: 'none',
} as const;

const getHeaderBackgroundColor = (variant: SectionVariant) =>
  variant === 'secondary' ? orangeColors[5] : greenColors[50];

const sectionHeaderTitleSx = {
  fontSize: '16px',
  lineHeight: 1.2,
  fontWeight: 600,
  color: greenColors[700],
  minWidth: 0,
} as const;

const sectionHeaderMetaSx = {
  fontSize: '12px',
  lineHeight: 1.2,
  fontWeight: 700,
  color: greenColors[700],
  flexShrink: 0,
  mr: 0.5,
} as const;

const sectionBodySx = {
  px: 1,
  py: 1,
  bgcolor: 'white',
  border: `1px solid ${orangeColors[3]}`,
  borderRadius: '0 0 8px 8px',
} as const;

export const EmpionCollapsibleSection = ({
  title,
  children,
  collapsible = false,
  open = true,
  onToggle,
  metaText,
  titleSuffix,
  variant = 'default',
}: Props) => {
  const handleKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (!collapsible || !onToggle) {
      return;
    }
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onToggle();
    }
  };

  return (
    <Box sx={sectionShellSx}>
      <Box
        onClick={collapsible ? onToggle : undefined}
        onKeyDown={handleKeyDown}
        role={collapsible ? 'button' : undefined}
        tabIndex={collapsible ? 0 : undefined}
        aria-expanded={collapsible ? open : undefined}
        sx={{
          ...sectionHeaderRowSx,
          bgcolor: getHeaderBackgroundColor(variant),
          ...(collapsible ? {cursor: 'pointer'} : {}),
        }}
      >
        <Box sx={{display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 0}}>
          <Typography sx={sectionHeaderTitleSx}>{title}</Typography>
          {titleSuffix}
        </Box>
        {collapsible ? (
          <Box sx={{display: 'flex', alignItems: 'center', gap: 0.5}}>
            {metaText ? <Typography sx={sectionHeaderMetaSx}>{metaText}</Typography> : null}
            <ExpandMoreIcon
              sx={{
                fontSize: 20,
                color: greenColors[100],
                flexShrink: 0,
                transform: open ? 'rotate(180deg)' : 'none',
                transition: 'transform 0.2s ease',
              }}
            />
          </Box>
        ) : null}
      </Box>
      {collapsible ? (
        <Collapse in={open} timeout='auto' unmountOnExit>
          <Box sx={sectionBodySx}>{children}</Box>
        </Collapse>
      ) : (
        <Box sx={sectionBodySx}>{children}</Box>
      )}
    </Box>
  );
};
